/**
 * Project configuration management
 * Handles project identity and metadata
 */

import path from 'path';
import fs from 'fs-extra';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../utils/logger.js';

export interface ProjectConfig {
  project: {
    id: string;
    name: string;
    description?: string;
    created: string;
    updated: string;
  };
  version: string;
  settings?: Record<string, any>;
}

export class ProjectConfigManager {
  private configPath: string;
  private config: ProjectConfig | null = null;

  constructor(workflowDir: string) {
    this.configPath = path.join(workflowDir, 'config.json');
  }

  /**
   * Initialize project configuration
   */
  async init(options: {
    name?: string;
    description?: string;
    id?: string;
  } = {}): Promise<ProjectConfig> {
    // Check if config already exists
    if (await fs.exists(this.configPath)) {
      logger.info('Project config already exists, loading...');
      return this.load();
    }

    // Create new config
    const now = new Date().toISOString();
    this.config = {
      project: {
        id: options.id || uuidv4(),
        name: options.name || path.basename(process.cwd()),
        description: options.description,
        created: now,
        updated: now,
      },
      version: '1.0',
      settings: {},
    };

    await this.save();
    logger.success(`Project initialized with ID: ${this.config.project.id}`);
    return this.config;
  }

  /**
   * Load project configuration
   */
  async load(): Promise<ProjectConfig> {
    if (!await fs.exists(this.configPath)) {
      throw new Error('Project config not found. Run "workflow init" first.');
    }

    try {
      this.config = await fs.readJson(this.configPath);
      return this.config!;
    } catch (error) {
      logger.error('Failed to load project config:', error);
      throw new Error('Invalid project configuration file');
    }
  }

  /**
   * Save project configuration
   */
  async save(): Promise<void> {
    if (!this.config) {
      throw new Error('No config to save');
    }

    // Update timestamp
    this.config.project.updated = new Date().toISOString();

    await fs.writeJson(this.configPath, this.config, { spaces: 2 });
    logger.debug('Project config saved');
  }

  /**
   * Get current project config
   */
  async get(): Promise<ProjectConfig | null> {
    if (!this.config && await fs.exists(this.configPath)) {
      await this.load();
    }
    return this.config;
  }

  /**
   * Update project metadata
   */
  async update(updates: Partial<ProjectConfig['project']>): Promise<ProjectConfig> {
    if (!this.config) {
      await this.load();
    }

    this.config!.project = {
      ...this.config!.project,
      ...updates,
      id: this.config!.project.id, // Prevent ID changes
      created: this.config!.project.created, // Preserve creation date
    };

    await this.save();
    return this.config!;
  }

  /**
   * Update project settings
   */
  async updateSettings(settings: Record<string, any>): Promise<ProjectConfig> {
    if (!this.config) {
      await this.load();
    }

    this.config!.settings = {
      ...this.config!.settings,
      ...settings,
    };

    await this.save();
    return this.config!;
  }

  /**
   * Check if project is initialized
   */
  async exists(): Promise<boolean> {
    return fs.exists(this.configPath);
  }

  /**
   * Get project ID
   */
  async getProjectId(): Promise<string | null> {
    const config = await this.get();
    return config?.project.id || null;
  }

  /**
   * Get project info for API responses
   */
  async getProjectInfo(): Promise<{
    id: string;
    name: string;
    description?: string;
  } | null> {
    const config = await this.get();
    if (!config) return null;

    return {
      id: config.project.id,
      name: config.project.name,
      description: config.project.description,
    };
  }
}